import { reportError } from "./sentry-reporter";

export const USER_ACTIVITY_FLUSH_INTERVAL_MS = 10_000;

type UserActivityListener = () => void | Promise<void>;

const TASKPANE_ACTIVITY_EVENT_TYPES = ["pointerdown", "pointermove", "keydown", "wheel", "focus"] as const;

const TASKPANE_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

const listeners = new Set<UserActivityListener>();

let activityDirty = false;

let flushTimer: ReturnType<typeof setInterval> | undefined;

export function reportUserActivity(): void {
  activityDirty = true;
}

function reportVisibleTaskpane(): void {
  if (document.visibilityState === "visible") reportUserActivity();
}

function notifyListener(listener: UserActivityListener): void {
  Promise.resolve()
    .then(listener)
    .catch((error: unknown) => {
      reportError(error, { extra: { component: "user-activity", action: "notify_listener" } });
    });
}

function flushActivity(): void {
  if (!activityDirty) return;
  activityDirty = false;
  listeners.forEach(notifyListener);
}

function startMonitoring(): void {
  flushTimer = setInterval(flushActivity, USER_ACTIVITY_FLUSH_INTERVAL_MS);
  TASKPANE_ACTIVITY_EVENT_TYPES.forEach((eventType) =>
    window.addEventListener(eventType, reportUserActivity, TASKPANE_LISTENER_OPTIONS),
  );
  document.addEventListener("visibilitychange", reportVisibleTaskpane);
}

function stopMonitoring(): void {
  clearInterval(flushTimer);
  flushTimer = undefined;
  activityDirty = false;
  TASKPANE_ACTIVITY_EVENT_TYPES.forEach((eventType) =>
    window.removeEventListener(eventType, reportUserActivity, TASKPANE_LISTENER_OPTIONS),
  );
  document.removeEventListener("visibilitychange", reportVisibleTaskpane);
}

export function subscribeToUserActivity(listener: UserActivityListener): () => void {
  if (listeners.size === 0) startMonitoring();
  listeners.add(listener);
  return function unsubscribeFromUserActivity() {
    if (!listeners.delete(listener)) return;
    if (listeners.size === 0) stopMonitoring();
  };
}
